import {
  ACTION_BUSINESS_CLICK_RESERVATION_TITLE,
  ACTION_BUSINESS_SELECT_EXPERIENCE,
  EventCustomAttributes,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import {
  ACTION_BUSINESS_OFFERING_CLICKED,
  CATEGORY_BUSINESS_PAGE,
} from 'utils/analytics/eventTypesConstants';
import { AmexIcon } from '@tock/shared/components/icons/CreditCardIcons';
import { Carousel, ExpandableParagraph, Typography } from 'omakase';
import { ExperienceOfferingDetailsLabel } from 'components/business/offering/ExperienceOfferingDetailsLabel';
import { IconCashPlus, IconLogoAmericanExpress, IconTableSeating } from '@tock/icons';
import { IconMigrationUtil, useIsSakeIconSetEnabled } from 'omakase/utils/IconMigrationUtil';
import { IconMoney, IconTablesRooms } from 'ui/icons';
import { OfferingDetailsFees } from 'components/business/offering/OfferingDetailsFees';
import { OfferingDetailsPrice } from 'components/business/offering/OfferingDetailsPrice';
import { Theme, css } from '@emotion/react';
import { consumer, core } from '@tock/coretypes';
import { experienceOfferingToData } from 'utils/analytics/amplitude';
import { getAutoFormatSrcAndSrcSetForWidthPx } from '@tock/images';
import { getRemainingEventTicketText, isOfferingEventType } from 'utils/offerings';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useConsumerConversionAnalytics } from 'hooks/analytics/useConsumerConversionAnalytics';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useHistory } from 'react-router-dom';
import LargeImageGallery from 'components/ui/LargeImageGallery';
import OfferingDetailsAdditionalCosts from 'components/business/offering/OfferingDetailsAdditionalCosts';
import OfferingDetailsMetadataItem from 'components/business/offering/OfferingDetailsMetadataItem';
import OfferingDetailsType from 'components/business/offering/OfferingDetailsType';
import OfferingHeading from 'components/business/offering/OfferingHeading';

type ClickContext = EventCustomAttributes['clickContext'];

interface Props {
  business: consumer.IBusinessConfig;
  offering: consumer.Offering;
  isShowingOnBusinessPage?: boolean;
  onNavigateToExperience?(event: consumer.Offering, nextAvailableDate?: string): void;
  remainingTickets?: number;
  linkTo?: string;
  onClick?(): void;
  label?: string;
  cutoffLength?: number;
  listIndex?: number;
  showExperienceImages?: boolean;
  isReschedule?: boolean;
}

export const ExperienceOfferingDetails: React.FC<Props> = ({
  onNavigateToExperience,
  offering,
  business,
  cutoffLength,
  isShowingOnBusinessPage,
  label,
  linkTo,
  onClick,
  remainingTickets,
  listIndex,
  showExperienceImages = true,
  isReschedule,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const history = useHistory();
  const isSakeIconsEnabled = useIsSakeIconSetEnabled();
  const isVariableDepositPricingEnabled = useFeatureFlag('LARGE_PARTY_VARIABLE_PRICING_DEPOSITS');
  const sendConversionAnalytics = useConsumerConversionAnalytics();

  const onClickTarget = (target: ClickContext) => {
    window.analytics.sendEvent(CATEGORY_BUSINESS_PAGE, ACTION_BUSINESS_OFFERING_CLICKED);
    amplitudeSendEvent(ACTION_BUSINESS_SELECT_EXPERIENCE, {
      listIndex,
      clickContext: target,
      ...experienceOfferingToData(offering),
    });
    sendConversionAnalytics(ACTION_BUSINESS_CLICK_RESERVATION_TITLE);
  };

  if (isOfferingEventType(offering)) {
    return null;
  }

  const renderSeatingAreas = (seatingAreas: core.IAvailabilitySeatingArea[]): React.ReactNode => {
    if (seatingAreas.length < 2) {
      return null;
    }
    return (
      <li className="Consumer-reservationMetaItem">
        <IconMigrationUtil
          legacyIcon={<IconTablesRooms className="Consumer-reservationMetaItemIcon" />}
          newIcon={<IconTableSeating className="Consumer-reservationMetaItemIcon" />}
        />{' '}
        <Typography component="div" variant="body02" color="fgDefault">
          {seatingAreas
            .map((seatingArea) => seatingArea.name)
            .sort()
            .join(', ')}
        </Typography>
      </li>
    );
  };

  const dataTestIdValue = offering.name.replace(/\W/g, '');

  const {
    description,
    descriptiveVariety,
    isAmexOnly,
    price,
    singleUnitQuantity,
    seatingArea,
    type,
    image,
  } = offering;

  const showLegacyFeeDisplay = price?.priceDisplayStyle === 'BASE_TICKET_PRICE';
  const showItemizedFeeDisplay = price?.priceDisplayStyle === 'ITEMIZED';

  const navigate = !!onNavigateToExperience;
  const remainingTicketsText = remainingTickets
    ? getRemainingEventTicketText(remainingTickets)
    : null;

  const shouldShowImgGallery = showExperienceImages && !isShowingOnBusinessPage && image.length > 0;

  const carouselSlides =
    image.length > 0
      ? image.map((i: core.IUploadedImage) => {
          const { src, srcSet } = getAutoFormatSrcAndSrcSetForWidthPx(
            { width: 600 },
            i.imageBackingUrl ?? undefined
          );
          return { src, srcSet, alt: i.altText ?? '', type: 'image' as const };
        })
      : undefined;

  const shouldShowCarousel =
    showExperienceImages && isShowingOnBusinessPage && image.length > 0 && carouselSlides;

  const getCarouselOnClick = () => {
    if (navigate && !linkTo) {
      return () => {
        onNavigateToExperience?.(offering);
        onClickTarget('Image');
      };
    }
    if (!navigate && linkTo) {
      return () => {
        onClick && onClick();
        onClickTarget('Image');
        history.push(linkTo);
      };
    }
    return undefined;
  };

  const carouselOnClick = getCarouselOnClick();

  let showPricingAsPartiesXPlus = !!offering.largePartyConfig;
  let minPartySizeWithPrice = offering.largePartyConfig?.minPartySize;
  if (isVariableDepositPricingEnabled) {
    const lowestPartySizeIsFree =
      !!price?.partyRangeConfigs &&
      price?.partyRangeConfigs?.length > 0 &&
      price?.partyRangeConfigs?.[0]?.ticketPriceInformation?.priceType === 'FREE';

    const nextLowestIsDeposit =
      !!lowestPartySizeIsFree &&
      !!price?.partyRangeConfigs &&
      price?.partyRangeConfigs?.length > 1 &&
      price?.partyRangeConfigs?.[1]?.ticketPriceInformation?.priceType === 'DEPOSIT';

    showPricingAsPartiesXPlus = lowestPartySizeIsFree && nextLowestIsDeposit;

    minPartySizeWithPrice = showPricingAsPartiesXPlus
      ? (price?.partyRangeConfigs?.[1]?.minPartySize ?? 0)
      : 0;
  }

  return (
    <div className="Consumer-resultsExperienceDetails">
      {shouldShowImgGallery && (
        <LargeImageGallery
          show={1}
          business={business as consumer.BusinessConfig}
          images={image as core.UploadedImage[]}
          isLightboxEnabled={image.length > 1}
          aspect={2.5}
          forceShowChipLabel
        />
      )}
      <div
        css={({ breakpoints, spacing, colors }: Theme) => [
          showExperienceImages &&
            !isShowingOnBusinessPage &&
            css`
              border-bottom: 1px solid ${colors.borderDefault};
              padding: ${spacing(1.5)}px ${spacing(2.5)}px ${spacing(2.5)}px;
              ${breakpoints.up('sm')} {
                padding: ${spacing(2)}px ${spacing(3.5)}px ${spacing(3.5)}px;
              }
            `,
          shouldShowCarousel &&
            css`
              display: flex;
              flex-direction: column;
              ${breakpoints.up('sm')} {
                display: grid;
                grid-template-columns: 1fr minmax(250px, 0.75fr);
                column-gap: 30px;
              }
            `,
        ]}
      >
        <div>
          <div className="Consumer-reservationInfo">
            <ExperienceOfferingDetailsLabel offering={offering} />

            <OfferingHeading
              isShowingOnBusinessPage={isShowingOnBusinessPage}
              offering={offering}
              navigate={navigate}
              linkTo={linkTo}
              dataTestIdValue={dataTestIdValue}
              onClickTarget={onClickTarget}
              onClick={onClick}
              onNavigateToExperience={onNavigateToExperience}
              label={label}
            />

            {description && (
              <ExpandableParagraph
                paragraphText={description}
                linkify
                cutoffLength={cutoffLength}
              />
            )}
          </div>

          <ul className="Consumer-reservationMetaList">
            {isAmexOnly && (
              <li className="Consumer-reservationMetaItem">
                <div className="Consumer-reservationMetaItemIcon">
                  <IconMigrationUtil
                    legacyIcon={
                      <AmexIcon contextClassName="Consumer-reservationMetaItemIcon-amexExclusive" />
                    }
                    newIcon={<IconLogoAmericanExpress />}
                  />
                </div>
                <Typography component="h2" variant="body02">
                  American Express® Card exclusive
                </Typography>
              </li>
            )}
            {type && (
              <OfferingDetailsType
                offering={offering}
                businessType={business.businessType ?? undefined}
              />
            )}
            {seatingArea && renderSeatingAreas(seatingArea)}
            {type && !showPricingAsPartiesXPlus && (
              <OfferingDetailsPrice
                price={price as consumer.OfferedPriceSummary}
                type={type}
                descriptiveVariety={descriptiveVariety}
                singleUnitQuantity={singleUnitQuantity}
                isReschedule={isReschedule}
              />
            )}
            {type && showLegacyFeeDisplay && (
              <OfferingDetailsAdditionalCosts
                price={price as consumer.OfferedPriceSummary}
                type={type}
              />
            )}
            {showItemizedFeeDisplay && (price?.fees ?? []).length > 0 && (
              <OfferingDetailsFees fees={price?.fees ?? []} />
            )}
            {showPricingAsPartiesXPlus && (
              <OfferingDetailsMetadataItem
                data-testid="large-party-note"
                icon={isSakeIconsEnabled ? IconCashPlus : IconMoney}
              >
                Parties {minPartySizeWithPrice}+ require a deposit
              </OfferingDetailsMetadataItem>
            )}
          </ul>

          {remainingTicketsText && (
            <Typography component="p" className="Consumer-reservationMetaList" variant="body02">
              {remainingTicketsText}
            </Typography>
          )}
        </div>

        {shouldShowCarousel && (
          <div
            css={({ spacing, breakpoints }: Theme) => css`
              position: relative;
              aspect-ratio: 1.75 / 1;
              ${breakpoints.down('xs')} {
                order: -1;
                margin-bottom: ${spacing(2)}px;
              }
            `}
          >
            <Carousel
              slideClickHandling={
                carouselOnClick
                  ? {
                      onClick: carouselOnClick,
                      role: 'link',
                      'aria-label': 'Book now (opens a dialog)',
                    }
                  : undefined
              }
              slides={carouselSlides}
              showArrowsDesktop="onmouseover"
            />
          </div>
        )}
      </div>
    </div>
  );
};
